import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { AlertTriangle, ShieldAlert, Loader2, Download, FileText } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Modale bloquante — purge l'historique de sécurité (audit_logs) sur une
 * période au choix, ou intégralement. Réservée aux administrateurs.
 *
 * Props :
 *  - open, onOpenChange
 *  - onDone(deletedCount) : callback après succès (pour recharger la liste)
 *  - downloadExportCsv(): fonction déjà présente dans la page pour exporter le
 *    CSV filtré sur la période courante — on l'expose côté modale afin de
 *    proposer un snapshot avant purge, cohérent avec les filtres de l'écran.
 */
export default function PurgeAuditHistoryDialog({ open, onOpenChange, onDone }) {
  const [scope, setScope] = useState("range"); // "range" | "all"
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);
  const [preview, setPreview] = useState({ loading: false, count: null });

  useEffect(() => {
    if (open) {
      setScope("range");
      setStartDate("");
      setEndDate("");
      setConfirmText("");
      setBusy(false);
      setExporting(false);
      setExported(false);
      setPreview({ loading: false, count: null });
    }
  }, [open]);

  const rangeParams = useCallback(() => {
    const p = {};
    if (scope === "range") {
      if (startDate) p.date_from = startDate;
      if (endDate) p.date_to = endDate;
    }
    return p;
  }, [scope, startDate, endDate]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setPreview((p) => ({ ...p, loading: true }));
      try {
        const { data } = await api.get("/audit-logs/purge-preview", {
          params: rangeParams(),
        });
        if (!cancelled) setPreview({ loading: false, count: data.count });
      } catch {
        if (!cancelled) setPreview({ loading: false, count: null });
      }
    })();
    return () => { cancelled = true; };
  }, [open, rangeParams]);

  const isConfirmed = confirmText.trim().toUpperCase() === "PURGE";
  const rangeInvalid =
    scope === "range" && startDate && endDate && startDate > endDate;
  const nothingToPurge = !preview.loading && preview.count === 0;

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      const p = rangeParams();
      if (p.date_from) params.set("date_from", p.date_from);
      if (p.date_to) params.set("date_to", p.date_to);
      const resp = await api.get(`/audit-logs/export.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `audit-logs-avant-purge_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExported(true);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error("Erreur lors de l'export CSV");
    } finally {
      setExporting(false);
    }
  };

  const submit = async () => {
    if (!isConfirmed) {
      toast.error("Tapez PURGE pour confirmer.");
      return;
    }
    if (rangeInvalid) {
      toast.error("La date de début doit être antérieure à la date de fin.");
      return;
    }
    setBusy(true);
    try {
      const params = { confirm: "PURGE", ...rangeParams() };
      const { data } = await api.post("/audit-logs/purge", null, { params });
      toast.success(`Purge effectuée : ${data.deleted} entrée(s) supprimée(s)`);
      onDone?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la purge");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="purge-audit-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-red-700">
            <ShieldAlert size={20} className="text-red-600" />
            Purger l&apos;historique de sécurité
          </DialogTitle>
          <DialogDescription>
            Supprime définitivement les événements de la collection
            <span className="font-mono text-xs mx-1">audit_logs</span>
            (connexions, réinitialisations, changements de mot de passe). L&apos;opération
            elle-même sera journalisée pour traçabilité.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-700">Périmètre</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setScope("range")}
                data-testid="audit-purge-scope-range"
                className={`flex-1 px-3 py-2 border rounded-sm text-sm transition ${
                  scope === "range"
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                Filtrer par période
              </button>
              <button
                type="button"
                onClick={() => setScope("all")}
                data-testid="audit-purge-scope-all"
                className={`flex-1 px-3 py-2 border rounded-sm text-sm transition ${
                  scope === "all"
                    ? "bg-red-600 text-white border-red-700"
                    : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                Tout supprimer
              </button>
            </div>
          </div>

          {scope === "range" && (
            <div className="grid grid-cols-2 gap-3" data-testid="audit-purge-range-fields">
              <div>
                <Label htmlFor="audit-purge-start" className="text-xs">Date début</Label>
                <Input
                  id="audit-purge-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="audit-purge-start-date"
                />
              </div>
              <div>
                <Label htmlFor="audit-purge-end" className="text-xs">Date fin (incluse)</Label>
                <Input
                  id="audit-purge-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="audit-purge-end-date"
                />
              </div>
              <p className="col-span-2 text-[11px] text-zinc-500 font-mono">
                Laissez un champ vide pour ne pas borner ce côté (ex. tout avant le 31/12).
              </p>
            </div>
          )}

          <div
            className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 space-y-2"
            data-testid="audit-purge-preview-card"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-zinc-800">
                <FileText size={14} className="text-zinc-500" />
                <span className="font-medium">Aperçu de la purge</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadCsv}
                disabled={exporting || preview.loading || nothingToPurge}
                data-testid="audit-purge-export-csv-btn"
                className="h-8"
              >
                {exporting ? (
                  <>
                    <Loader2 size={13} className="mr-1.5 animate-spin" /> Export…
                  </>
                ) : (
                  <>
                    <Download size={13} className="mr-1.5" />
                    Exporter CSV avant purge
                  </>
                )}
              </Button>
            </div>
            {preview.loading ? (
              <p className="text-xs text-zinc-500 font-mono inline-flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Comptage en cours…
              </p>
            ) : preview.count === null ? (
              <p className="text-xs text-zinc-500 font-mono">—</p>
            ) : (
              <div className="text-xs text-zinc-700 font-mono" data-testid="audit-purge-preview-count">
                <span className="text-red-700 font-semibold">{preview.count}</span> entrée(s) d&apos;historique concernée(s)
                {nothingToPurge && (
                  <span className="ml-2 text-emerald-700">— rien à supprimer</span>
                )}
              </div>
            )}
            {exported && (
              <p className="text-[11px] text-emerald-700 font-mono">
                ✓ CSV téléchargé, vous pouvez purger en toute sécurité.
              </p>
            )}
          </div>

          <Alert className="border-amber-300 bg-amber-50 text-amber-900">
            <AlertTriangle size={16} className="text-amber-700" />
            <AlertTitle className="text-amber-900">Action irréversible</AlertTitle>
            <AlertDescription className="text-amber-800 text-xs">
              La suppression est définitive. Une entrée
              <span className="font-mono mx-1">audit_purge</span>
              sera automatiquement enregistrée avec l&apos;auteur, la période et
              le nombre d&apos;entrées supprimées.
            </AlertDescription>
          </Alert>

          <div className="space-y-1">
            <Label htmlFor="audit-purge-confirm" className="text-xs">
              Tapez <span className="font-mono font-bold text-red-700">PURGE</span> pour confirmer
            </Label>
            <Input
              id="audit-purge-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="PURGE"
              autoComplete="off"
              data-testid="audit-purge-confirm-input"
            />
          </div>

          {rangeInvalid && (
            <p className="text-xs text-red-600 font-medium">
              La date de début doit être antérieure ou égale à la date de fin.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="audit-purge-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !isConfirmed || rangeInvalid}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="audit-purge-submit-btn"
          >
            {busy ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" /> Purge en cours…
              </>
            ) : (
              "Purger maintenant"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
